import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { DeviceSecurityPanel } from '../components/DeviceSecurityPanel';
import { PolicyResultPanel, ReachabilityPanel, WipePanel, DeleteDevicePanel } from '../components/DevicePanels';
import { AssignmentPanel } from '../components/AssignmentPanel';
import type { Capability, Device, Policy } from '../lib/api';
import {
  DeploymentStatusBadge,
  Empty,
  ErrorNotice,
  Loading,
  ManagementBadge,
  Notice,
  OnlineBadge,
  Panel,
  PolicyStatusBadge,
  formatBytes,
  formatDateTime,
  relativeTime,
  useAsync,
} from '../components/ui';

interface DeviceDetail {
  device: Device;
  capabilities: Capability[];
  checkIns: Array<{
    created_at: string; battery_level: number | null; is_charging: boolean | null;
    network_type: string | null; wifi_ssid: string | null; kiosk_active: boolean | null;
    agent_version: string | null;
  }>;
  commands: Array<{
    id: string; command_type: string; status: string; error_message: string | null;
    dispatch_count: number; created_at: string; completed_at: string | null;
  }>;
  deployments: Array<{
    id: string; status: string; status_message: string | null; deployment_id: string;
    version_name: string; version_code: number; package_name: string; display_name: string;
    created_at: string;
  }>;
  installedApplications: Array<{
    package_name: string; version_code: number | null; version_name: string | null;
    is_managed: boolean; last_seen_at: string;
  }>;
}

export default function DeviceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [message, setMessage] = useState<{ kind: 'success' | 'info'; text: string } | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const { data, error, loading, reload } = useAsync(
    (signal) => api.get<DeviceDetail>(`/devices/${id}`, signal),
    [id],
    10_000,
  );
  const policies = useAsync((signal) => api.get<{ policies: Policy[] }>('/policies', signal), []);

  const sendCommand = async (
    command: string,
    confirmText?: string,
    extra?: Record<string, unknown>,
  ) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setActionError(null);
    setMessage(null);
    try {
      const result = await api.post<{ message: string; deviceOnline: string }>(
        `/devices/${id}/commands`,
        { command, ...extra },
      );
      // The message is written by the server and states queued-vs-delivered
      // honestly, including when the device is offline.
      setMessage({ kind: result.deviceOnline === 'online' ? 'success' : 'info', text: result.message });
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  const assignPolicy = async (policyId: string) => {
    if (!policyId) return;
    setActionError(null);
    setMessage(null);
    try {
      const result = await api.post<{ message: string }>(`/policies/${policyId}/assign`, {
        deviceIds: [id],
      });
      setMessage({ kind: 'info', text: result.message });
      reload();
    } catch (err) {
      setActionError(err);
    }
  };

  if (loading && !data) return <Loading what="device" />;
  if (error) return <ErrorNotice error={error} />;
  if (!data) return <Empty>Device not found.</Empty>;

  const d = data.device;
  const unsupported = data.capabilities.filter((c) => c.status !== 'supported');

  return (
    <>
      <div className="page-head">
        <div>
          {/* The person is the heading when there is one; the hardware is the
              subtitle. A device with no assignment keeps its previous heading
              rather than showing an empty one. */}
          <h2>{d.student?.studentName ?? d.displayName}</h2>
          <p>
            {d.student?.studentRef && (
              <>
                <span className="mono">{d.student.studentRef}</span> ·{' '}
              </>
            )}
            {[d.manufacturer, d.model].filter(Boolean).join(' ')} ·{' '}
            Android {d.androidVersion} (API {d.androidSdkInt}) · <Link to="/devices">back to devices</Link>
          </p>
        </div>
        <div className="actions">
          <OnlineBadge state={d.online} />
          <ManagementBadge state={d.managementState} />
        </div>
      </div>

      {message && <Notice kind={message.kind}>{message.text}</Notice>}
      <ErrorNotice error={actionError} />

      {d.managementState !== 'device_owner' && (
        <Notice kind="warning">
          <strong>This device is not a Device Owner.</strong>
          <div>
            Kiosk mode and most restrictions cannot be enforced. The agent reports its own
            Device Owner state on every check-in, so this reflects the device as of{' '}
            {relativeTime(d.lastSeenAt)}.
          </div>
        </Notice>
      )}

      {d.lastError && (
        <Notice kind="error">
          <strong>Last reported error</strong>
          <div>{d.lastError.message}</div>
          <div className="small">{formatDateTime(d.lastError.at)}</div>
        </Notice>
      )}

      {/* Assignment before hardware: the first question about a tablet during an
          exam is who has it. Student metadata is kept in its own panel rather
          than mixed into "Device information", which is asset data. */}
      <AssignmentPanel device={d} onChanged={reload} />

      <ReachabilityPanel device={d} />

      <div className="grid cols-2">
        <Panel title="Device information">
          <dl className="kv">
            <dt>Enrollment</dt><dd>{d.enrollmentStatus}</dd>
            <dt>Management</dt><dd><ManagementBadge state={d.managementState} /></dd>
            <dt>Serial</dt><dd className="mono">{d.serialNumber ?? '—'}</dd>
            <dt>Asset tag</dt><dd>{d.assetTag ?? '—'}</dd>
            <dt>OS build</dt><dd className="mono small">{d.osBuild ?? '—'}</dd>
            <dt>Security patch</dt><dd>{d.securityPatchLevel ?? '—'}</dd>
            <dt>Agent version</dt><dd>{d.agentVersion ?? '—'}</dd>
            <dt>Enrolled</dt><dd>{formatDateTime(d.enrolledAt)}</dd>
            <dt>Last seen</dt><dd>{formatDateTime(d.lastSeenAt)} <span className="muted small">({relativeTime(d.lastSeenAt)})</span></dd>
            <dt>Battery</dt><dd>{d.battery ? `${d.battery.level}%${d.battery.charging ? ' (charging)' : ''}` : '—'}</dd>
            <dt>Network</dt><dd>{d.network.type ?? '—'}{d.network.wifiSsid ? ` · ${d.network.wifiSsid}` : ''}</dd>
            <dt>Storage free</dt><dd>{d.storage ? `${formatBytes(d.storage.freeBytes)} of ${formatBytes(d.storage.totalBytes)}` : '—'}</dd>
            <dt>Kiosk requested</dt>
            <dd>{d.kioskActive === null ? 'unknown' : d.kioskActive ? 'yes' : 'no'}</dd>
            {/*
              Intent and observation shown as two rows on purpose. The device
              reads the real lock-task state back from ActivityManager, so a
              kiosk that was requested and never engaged is visible here instead
              of being reported as a secured tablet.
            */}
            <dt>Lock task verified</dt>
            <dd>
              {!d.kioskActive ? (
                <span className="muted">n/a</span>
              ) : d.kioskLockTaskVerified === true ? (
                <span className="badge ok">LOCKED</span>
              ) : d.kioskLockTaskVerified === false ? (
                <span className="badge danger">NOT LOCKED</span>
              ) : (
                <span className="badge muted">NOT REPORTED</span>
              )}
            </dd>
            <dt>Storage encryption</dt>
            <dd>
              {d.encryptionStatus
                ? <span className={`badge ${d.encryptionStatus.startsWith('ACTIVE') ? 'ok' : 'warn'}`}>
                    {d.encryptionStatus}
                  </span>
                : <span className="muted">not reported</span>}
            </dd>
            <dt>Enrolment count</dt>
            <dd>
              {d.enrollmentCount}
              {d.enrollmentCount > 1 && (
                <span className="muted small"> · last {relativeTime(d.lastEnrolledAt)}</span>
              )}
            </dd>
          </dl>
        </Panel>

        <Panel title="Policy">
          {d.policy ? (
            <dl className="kv">
              <dt>Assigned</dt>
              <dd><Link to={`/policies/${d.policy.id}`}>{d.policy.name}</Link> v{d.policy.version}</dd>
              <dt>Enforcement</dt><dd><PolicyStatusBadge status={d.policy.status} /></dd>
              <dt>Applied version</dt><dd>{d.policy.appliedVersion ?? 'not yet applied'}</dd>
              <dt>Applied at</dt><dd>{formatDateTime(d.policy.appliedAt)}</dd>
            </dl>
          ) : (
            <Empty>No policy assigned.</Empty>
          )}

          {d.policy?.status === 'partial' && (
            <Notice kind="warning">
              <strong>This policy was processed but is not fully in force.</strong>
              <div>
                The tablet acknowledged {d.policy.name}, but at least one control failed —
                often a leftover kiosk restriction that could not be cleared. See Policy
                control results below; do not treat this device as matching TEST until those
                rows are SKIPPED or APPLIED, not FAILED.
              </div>
            </Notice>
          )}
          {d.policy?.status === 'failed' && (
            <Notice kind="error">
              <strong>The assigned policy is not enforced on this device.</strong>
            </Notice>
          )}
          {d.policy?.status === 'pending' && (
            <Notice kind="info">
              Assigned. Waiting for the device to apply it at the next check-in.
            </Notice>
          )}

          <div style={{ marginTop: 12 }}>
            <label>
              <span>Change policy</span>
              <select defaultValue="" onChange={(e) => assignPolicy(e.target.value)}>
                <option value="">Select a policy…</option>
                {(policies.data?.policies ?? []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} (v{p.version}, {p.kind}{p.validated_at ? '' : ', unvalidated'})
                  </option>
                ))}
              </select>
            </label>
            <p className="small muted" style={{ margin: 0 }}>
              Assigning queues the policy. It applies at the device's next check-in.
            </p>
          </div>
        </Panel>
      </div>

      {d.policy && (
        <Panel title={`What this policy can actually enforce on Android ${d.androidVersion}`}>
          {unsupported.length === 0 ? (
            <Notice kind="success">
              Every control in this policy is supported on this device's Android version.
            </Notice>
          ) : (
            <Notice kind="warning">
              <strong>{unsupported.length} control(s) will NOT take effect on this device.</strong>
              <div>Android silently ignores these, so they are excluded from what is sent.</div>
            </Notice>
          )}
          <div className="table-wrap">
            <table>
              <thead>
                <tr><th>Control</th><th>Status</th><th>Android mechanism</th><th>Needs API</th></tr>
              </thead>
              <tbody>
                {data.capabilities.map((c) => (
                  <tr key={c.key}>
                    <td>
                      {c.label}
                      {c.reason && <div className="small muted">{c.reason}</div>}
                    </td>
                    <td>
                      {c.status === 'supported'
                        ? <span className="badge ok">SUPPORTED</span>
                        : <span className="badge danger">UNSUPPORTED</span>}
                    </td>
                    <td className="mono small">{c.androidMechanism}</td>
                    <td className="num">{c.minApiLevel}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      <PolicyResultPanel device={d} />

      <DeviceSecurityPanel deviceId={d.id} />

      <Panel title="Actions">
        <div className="actions">
          <button onClick={() => sendCommand('collect_status')}>Refresh status</button>
          <button onClick={() => sendCommand('apply_policy')}>Re-apply policy</button>
          <button onClick={() => sendCommand('start_kiosk')}>Start kiosk</button>
          <button onClick={() => sendCommand('stop_kiosk')}>Stop kiosk</button>
          <button onClick={() => sendCommand('lock_device', `Lock ${d.displayName} now?`)}>Lock</button>
          <button
            className="danger"
            onClick={() =>
              sendCommand(
                'reboot_device',
                `Reboot ${d.displayName}? If an exam is in progress this will interrupt it.`,
              )
            }
          >
            Reboot
          </button>
        </div>
        <p className="small muted" style={{ marginBottom: 0, marginTop: 10 }}>
          Commands are queued, not executed immediately. This provider has no push channel, so a
          command reaches the device at its next check-in — and waits if the device is offline.
          Only commands the active provider genuinely supports are shown.
        </p>
      </Panel>

      <WipePanel device={d} onWipe={sendCommand} />
      <DeleteDevicePanel device={d} onDeleted={() => navigate('/devices', { replace: true })} />

      <div className="grid cols-2">
        <Panel title="Installed applications (reported by the device)">
          {data.installedApplications.length === 0 ? (
            <Empty>The device has not reported its application inventory yet.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Package</th><th>Version</th><th>Managed</th><th>Actions</th></tr></thead>
                <tbody>
                  {data.installedApplications.map((a) => (
                    <tr key={a.package_name}>
                      <td className="mono small">{a.package_name}</td>
                      <td>{a.version_name ?? '—'}<span className="muted small"> ({a.version_code ?? '—'})</span></td>
                      <td>{a.is_managed ? <span className="badge info">MANAGED</span> : <span className="muted small">—</span>}</td>
                      <td className="nowrap">
                        {/*
                          Both commands are implemented end to end in the backend
                          and the agent; they simply had no control here. Offered
                          per package because both need a package name, and the
                          reported inventory is the only trustworthy source of
                          one — typing a package by hand invites a typo that
                          silently does nothing.
                        */}
                        <button
                          className="small"
                          onClick={() =>
                            sendCommand(
                              'clear_app_data',
                              `Clear all data for ${a.package_name} on ${d.displayName}? ` +
                                'Exam progress, logins and cached content are erased; the ' +
                                'application itself stays installed. Requires Android 9 (API 28) ' +
                                'or later — older devices report the command as failed.',
                              { payload: { packageName: a.package_name } },
                            )
                          }
                        >
                          Clear data
                        </button>{' '}
                        <button
                          className="small danger"
                          onClick={() =>
                            sendCommand(
                              'uninstall_app',
                              `Uninstall ${a.package_name} from ${d.displayName}? ` +
                                'The application and its data are removed. If it is the kiosk ' +
                                'application, the device shows the kiosk shell with nothing to ' +
                                'launch until it is redeployed.',
                              { payload: { packageName: a.package_name } },
                            )
                          }
                        >
                          Uninstall
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="small muted" style={{ marginBottom: 0 }}>
            Both actions are queued and reach the device at its next check-in. Uninstall lifts the
            policy's uninstall block for that package first, so a managed application can be
            removed without editing the policy. Clearing data requires Android 9 or later — the
            device reports UNSUPPORTED rather than failing silently on older versions.
          </p>
        </Panel>

        <Panel title="Recent check-ins">
          {data.checkIns.length === 0 ? (
            <Empty>No check-ins recorded.</Empty>
          ) : (
            <ul className="timeline">
              {data.checkIns.slice(0, 12).map((c, i) => (
                <li key={i}>
                  <time>{formatDateTime(c.created_at)}</time>
                  <span className="small">
                    {c.battery_level !== null && `${c.battery_level}%`}
                    {c.is_charging ? ' charging' : ''}
                    {c.network_type && ` · ${c.network_type}`}
                    {c.wifi_ssid && ` (${c.wifi_ssid})`}
                    {c.kiosk_active !== null && ` · kiosk ${c.kiosk_active ? 'on' : 'off'}`}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      <div className="grid cols-2">
        <Panel title="Recent commands">
          {data.commands.length === 0 ? (
            <Empty>No commands issued.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Command</th><th>Status</th><th>Created</th></tr></thead>
                <tbody>
                  {data.commands.map((c) => (
                    <tr key={c.id}>
                      <td>
                        {c.command_type}
                        {c.error_message && <div className="small" style={{ color: 'var(--danger)' }}>{c.error_message}</div>}
                      </td>
                      <td><DeploymentStatusBadge status={c.status} /></td>
                      <td className="small muted">{relativeTime(c.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>

        <Panel title="Deployment history">
          {data.deployments.length === 0 ? (
            <Empty>No deployments to this device.</Empty>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Application</th><th>Version</th><th>Status</th></tr></thead>
                <tbody>
                  {data.deployments.map((t) => (
                    <tr key={t.id}>
                      <td><Link to={`/deployments/${t.deployment_id}`}>{t.display_name}</Link></td>
                      <td>v{t.version_name}</td>
                      <td>
                        <DeploymentStatusBadge status={t.status} />
                        {t.status_message && <div className="small muted">{t.status_message}</div>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>
    </>
  );
}
